// Módulo de Inventarios: consulta de ajustes de Sysplus, ajuste de precios y exportación a Excel.
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { TEMAS_GLASS, useTemaOscuro } from "../../../utils/tema";
import { aISO, isoDia, fmt } from "../../../utils/format";
import {
  API, POR_PAGINA, DEBOUNCE_MS, NEUTRO_CHIP, FILTROS_REVISION, RANGOS_RAPIDOS, ESTADOS, BTN_PRIMARY,
  txt, lineasDe, aFormatoFirebird, estilosTema,
} from "./utils/constantes";
import { pedirJson } from "./utils/api";
import { necesitaPrecio, tieneAlerta } from "./utils/reglasPrecio";
import { exportarLibro, mensajeExcel, nombreHojaUnico } from "./utils/exportarExcel";
import useAjustesFiltrados from "./hooks/useAjustesFiltrados";
import useVistos from "./hooks/useVistos";
import usePrecios from "./hooks/usePrecios";
import KpisInventario, { Stepper, SkeletonCards } from "./components/KpisInventario";
import TablaAjustes, { SkeletonTabla } from "./components/TablaAjustes";
import { Banner, BarraProgreso } from "./components/Banner";
import ModalDetalle from "./components/ModalDetalleAjuste";

export default function Invent({ onBackToMenu }) {
  const [temaOscuro, setTemaOscuro] = useTemaOscuro();
  const tema = temaOscuro ? TEMAS_GLASS.oscuro : TEMAS_GLASS.claro;
  const { btnGlass, inputGlass } = estilosTema(tema);

  const [ajustes, setAjustes] = useState([]);
  const [loading, setLoading] = useState(true); 
  const [detalle, setDetalle] = useState([]);
  const [mensaje, setMensaje] = useState(null);
  const [recarga, setRecarga] = useState(0);
  const [seleccionado, setSeleccionado] = useState(null);
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);
  const [vistaModal, setVistaModal] = useState("tabla");
  const [filtroRevision, setFiltroRevision] = useState("todos");

  // ✅ UNIFICAMOS EL HOOK: Le pasamos los ajustes y extraemos todo de él
  const lista = useAjustesFiltrados(ajustes); 
  const { 
    filtros, actualizar, filtrados, paginacion, 
    fechasTemporales, setFechasTemporales, aplicarFechas 
  } = lista;
  const { inicio, fin, busqueda, estado, bodega, concepto } = filtros;

  const vistos = useVistos();
  const { estaVisto, esPermanente, marcarSesion, togglePermanente, limpiarSesion, limpiarPermanentes, totalPermanentes } = vistos;

  useEffect(() => {
    limpiarSesion();
  }, [busqueda, estado, bodega, concepto, inicio, fin, filtroRevision, limpiarSesion]);

  const filtradosConRevision = useMemo(() => {
    if (filtroRevision === "pendientes") return filtrados.filter((a) => !estaVisto(a.AJUS_ID));
    if (filtroRevision === "revisados") return filtrados.filter((a) => estaVisto(a.AJUS_ID));
    return filtrados;
  }, [filtrados, filtroRevision, estaVisto]);

  const conteoRevision = useMemo(() => {
    const revisados = filtrados.filter((a) => estaVisto(a.AJUS_ID)).length;
    return { pendientes: filtrados.length - revisados, revisados };
  }, [filtrados, estaVisto]);

  const detallePorAjuste = useMemo(() => {
    const mapa = new Map();
    detalle.forEach((d) => {
      const id = String(d.AJUS_ID);
      if (!mapa.has(id)) mapa.set(id, []);
      mapa.get(id).push(d);
    });
    return mapa;
  }, [detalle]);

  const px = usePrecios({ detalle, detallePorAjuste, setMensaje });
  const { precios, reiniciar } = px;

  /* ------------------------------ Carga de datos ------------------------------ */
  const cargar = useCallback(
    async (signal) => {
      setLoading(true);
      setMensaje(null);
      
      try {
        const params = new URLSearchParams();
        if (inicio) params.append("inicio", aFormatoFirebird(inicio));
        if (fin) params.append("fin", aFormatoFirebird(fin));
        const qs = params.toString() ? `?${params}` : "";

        const [dataAj, dataDet] = await Promise.all([
          pedirJson(`${API}/ajustes${qs}`, { signal }),
          pedirJson(`${API}/ajustes/detalle${qs}`, { signal }),
        ]);
        
        const ajustesLimpios = Array.isArray(dataAj.data) ? dataAj.data : [];
        const detalleLimpio = Array.isArray(dataDet.data) ? dataDet.data : [];

        setAjustes(ajustesLimpios);
        setDetalle(detalleLimpio);
        reiniciar(); 
      } catch (err) {
        if (err.name === "AbortError") return;
        setMensaje({ type: "error", text: `No se pudieron cargar los ajustes: ${err.message}.` });
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [inicio, fin, reiniciar]
  );

  // El useEffect escucha los cambios de fecha (al presionar Aplicar) y la recarga manual
  useEffect(() => {
    const controller = new AbortController();
    const t = setTimeout(() => cargar(controller.signal), DEBOUNCE_MS);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [cargar, recarga]);

  useEffect(() => {
    if (!mensaje || mensaje.type === "error" || mensaje.type === "warning") return undefined;
    const t = setTimeout(() => setMensaje(null), 8000);
    return () => clearTimeout(t);
  }, [mensaje]);

  const cerrarModal = useCallback(() => setSeleccionado(null), []);
  const abrirAjuste = useCallback((ajuste) => { setSeleccionado(ajuste); marcarSesion(ajuste.AJUS_ID); }, [marcarSesion]);

  /* ------------------------------ Datos derivados ------------------------------ */
  const kpis = useMemo(() => ({
    total: ajustes.length,
    activos: ajustes.filter(ESTADOS.activos.pasa).length,
    anulados: ajustes.filter(ESTADOS.anulados.pasa).length,
  }), [ajustes]);

  const sparkVolumen = useMemo(() => {
    const porDia = new Map();
    ajustes.forEach((a) => {
      const d = isoDia(a.AJUS_FECHA);
      if (d) porDia.set(d, (porDia.get(d) || 0) + 1);
    });
    return [...porDia.keys()].sort().slice(-14).map((d) => porDia.get(d));
  }, [ajustes]);

  const ajustesConAlerta = useMemo(
    () => ajustes.filter((a) => tieneAlerta(lineasDe(detallePorAjuste, a), precios)).length,
    [ajustes, detallePorAjuste, precios]
  );

  const totalRevisados = useMemo(() => ajustes.filter((a) => estaVisto(a.AJUS_ID)).length, [ajustes, estaVisto]);
  const hayPrecios = Object.keys(precios).length > 0;

  const pendientesPrecio = useMemo(() => {
    let n = 0;
    filtrados.forEach((a) => lineasDe(detallePorAjuste, a).forEach((d) => {
      if (necesitaPrecio(d) && !precios[txt(d.ARTI_COD)]) n++;
    }));
    return n;
  }, [filtrados, detallePorAjuste, precios]);

  /* --------------------------------- Exportación --------------------------------- */
  const exportarAjuste = async (ajuste) => {
    try {
      const r = await exportarLibro({
        ajustes: [ajuste], detallePorAjuste, precios,
        archivo: `Ajuste_${ajuste.AJUS_ID}_${aISO(new Date())}.xlsx`,
        hojaResumen: "Ajuste", nombreHoja: () => "Detalle",
      });
      setMensaje(mensajeExcel(`Ajuste #${ajuste.AJUS_ID} exportado con ${fmt(r.lineas, 3)} línea(s).`, r));
    } catch (err) {
      setMensaje({ type: "error", text: `No se pudo generar el Excel del ajuste #${ajuste.AJUS_ID}: ${err.message}.` });
    }
  };

  const exportarTodos = async () => {
    if (filtradosConRevision.length === 0) return setMensaje({ type: "warning", text: "No hay ajustes para exportar." });

    if (pendientesPrecio > 0) {
      const texto = hayPrecios
        ? `Hay ${fmt(pendientesPrecio, 3)} líneas sin recalcular. ¿Deseas exportar igual?`
        : "No has recalculado los precios. ¿Deseas exportar igual?";
      if (!window.confirm(texto)) return setMensaje({ type: "warning", text: "Exportación cancelada." });
    }

    try {
      const r = await exportarLibro({
        ajustes: filtradosConRevision, detallePorAjuste, precios,
        archivo: `Ajustes_${aISO(new Date())}.xlsx`,
        hojaResumen: "Ajustes", nombreHoja: nombreHojaUnico,
      });
      setMensaje(mensajeExcel(`Se exportaron ${fmt(filtradosConRevision.length, 3)} ajustes.`, r));
    } catch (err) {
      setMensaje({ type: "error", text: `No se pudo generar el Excel: ${err.message}.` });
    }
  };

  const aplicarRangoRapido = (rango) => {
    const [ini, fn] = rango.calc();
    const fechaIni = ini ? aISO(ini) : "";
    const fechaFn = fn ? aISO(fn) : "";
    setFechasTemporales({ inicio: fechaIni, fin: fechaFn });
    actualizar({ inicio: fechaIni, fin: fechaFn }); // Los rangos rápidos aplican de una vez
  };

  const tarjetasKpi = [
    { label: "Total ajustes", valor: kpis.total, color: tema.textoFuerte, sparkColor: "#007aff", icono: "📋" },
    { label: "Activos", valor: kpis.activos, color: "text-emerald-700", sparkColor: "#10b981", icono: "🟢" },
    { label: "Anulados", valor: kpis.anulados, color: "text-red-700", sparkColor: "#ef4444", icono: "🔴" },
    { label: "Con alertas", valor: ajustesConAlerta, color: ajustesConAlerta > 0 ? "text-red-700" : tema.textoSuave, sparkColor: "#dc2626", icono: "⚠️" },
    { label: "Revisados", valor: totalRevisados, color: "text-emerald-700", sparkColor: "#10b981", icono: "✅" },
  ];

  const hayError = mensaje?.type === "error";
  const { setPagina } = paginacion;
  const totalPaginas = Math.max(1, Math.ceil(filtradosConRevision.length / POR_PAGINA));
  const paginaActual = Math.min(paginacion.paginaActual, totalPaginas);
  const desde = (paginaActual - 1) * POR_PAGINA;
  const visiblesRevision = filtradosConRevision.slice(desde, desde + POR_PAGINA);

  useEffect(() => { setPagina(1); }, [filtroRevision, setPagina]);

  return (
    <div
      data-invent-root
      aria-hidden={seleccionado ? "true" : undefined}
      className={`${tema.fondo} ${tema.borde} backdrop-blur-[35px] rounded-[34px] shadow-[0_25px_70px_rgba(0,0,0,0.12)] p-6 sm:p-10 max-w-7xl w-full mx-auto max-h-[92vh] overflow-y-auto border relative`}
    >
      <div className={`flex flex-col md:flex-row justify-between items-start md:items-center border-b ${tema.bordeInterno} pb-6 mb-6 gap-4`}>
        <div className="flex items-center gap-3.5">
          <div className={`w-12 h-12 rounded-2xl ${tema.inputBg} border ${tema.btnBorde} flex items-center justify-center text-2xl shadow-sm`}>📦</div>
          <div>
            <h2 className={`text-2xl font-extrabold tracking-tight ${tema.textoFuerte}`}>Módulo de Inventarios</h2>
            <p className={`text-sm font-medium mt-0.5 ${tema.textoSuave}`}>Consulta de ajustes desde Sysplus.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setTemaOscuro((v) => !v)} className={btnGlass} title={temaOscuro ? "Modo claro" : "Modo oscuro"}>
            {temaOscuro ? "☀️" : "🌙"}
          </button>
          <button onClick={() => setRecarga((n) => n + 1)} disabled={loading} className={btnGlass}>
            🔄 Refrescar
          </button>
          <button onClick={onBackToMenu} className={btnGlass}>
            Volver al Menú
          </button>
        </div>
      </div>

      {!loading && <Stepper pendientes={pendientesPrecio} hayPrecios={hayPrecios} totalAjustes={ajustes.length} tema={tema} />}

      {mensaje && <Banner mensaje={mensaje} onReintentar={() => setRecarga((n) => n + 1)} onCerrar={() => setMensaje(null)} />}
      {px.progreso && <BarraProgreso progreso={px.progreso} />}

      {loading ? (
        <><SkeletonCards tema={tema} /><SkeletonTabla tema={tema} /></>
      ) : hayError && ajustes.length === 0 ? null : (
        <>
          <KpisInventario tarjetasKpi={tarjetasKpi} sparkVolumen={sparkVolumen} tema={tema} />

          <div className="flex flex-wrap gap-2 mb-3">
            {RANGOS_RAPIDOS.map((rango) => (
              <button key={rango.id} onClick={() => aplicarRangoRapido(rango)} className={`px-4 py-2 rounded-full ${tema.btnBg} ${tema.btnTexto} text-[11px] font-bold border ${tema.btnBorde} shadow-sm cursor-pointer transition-colors`}>
                {rango.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3" role="group">
            {FILTROS_REVISION.map((f) => (
              <button key={f.id} onClick={() => setFiltroRevision(f.id)} aria-pressed={filtroRevision === f.id}
                className={`px-4 py-2 rounded-full text-[11px] font-bold border shadow-sm cursor-pointer transition-colors ${filtroRevision === f.id ? f.activo : NEUTRO_CHIP}`}>
                {f.label} {f.id !== "todos" ? ` (${fmt(conteoRevision[f.id], 3)})` : ""}
              </button>
            ))}
            {totalPermanentes > 0 && (
              <button onClick={limpiarPermanentes} className="px-4 py-2 rounded-full bg-red-500/10 hover:bg-red-500/20 text-red-800 text-[11px] font-bold border border-red-400/40 shadow-sm cursor-pointer transition-colors">
                🔄 Resetear permanentes ({fmt(totalPermanentes, 3)})
              </button>
            )}
          </div>

          <div className={`${tema.cardBg} ${tema.cardBorde} backdrop-blur-md rounded-3xl border shadow-sm p-4 mb-6`}>
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
              <input type="text" value={busqueda} onChange={(e) => actualizar({ busqueda: e.target.value })} placeholder="🔍 Buscar por ID, prefijo, bodega..." className={`${inputGlass} lg:flex-1`} />
              <button onClick={() => setFiltrosAbiertos((v) => !v)} className={`${btnGlass} whitespace-nowrap`} aria-expanded={filtrosAbiertos}>
                ⚙️ Filtros {lista.chips.length > 0 ? `(${lista.chips.length})` : ""} {filtrosAbiertos ? "▲" : "▼"}
              </button>
              <button onClick={() => { lista.limpiar(); setFiltroRevision("todos"); }} disabled={!lista.hayFiltros && filtroRevision === "todos"} className={`${btnGlass} whitespace-nowrap`} title="Limpiar filtros">
                🧹 Limpiar
              </button>
            </div>

            {filtrosAbiertos && (
              <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-3 pt-3 border-t ${tema.bordeSuave} inv-slide-up`}>
                <select value={bodega} onChange={(e) => actualizar({ bodega: e.target.value })} className={inputGlass}>
                  <option value="">Todas las bodegas</option>
                  {lista.bodegas.map((b) => <option key={b} value={b}>Bodega {b}</option>)}
                </select>
                <select value={concepto} onChange={(e) => actualizar({ concepto: e.target.value })} className={inputGlass}>
                  <option value="">Todos los conceptos</option>
                  {lista.conceptos.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={estado} onChange={(e) => actualizar({ estado: e.target.value })} className={inputGlass}>
                  {Object.entries(ESTADOS).map(([valor, { label }]) => <option key={valor} value={valor}>{label}</option>)}
                </select>
                
                {/* FECHAS CON BOTÓN APLICAR */}
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] uppercase font-bold text-gray-500 ml-1">Desde</label>
                  <input type="date" value={fechasTemporales.inicio} onChange={(e) => setFechasTemporales(f => ({ ...f, inicio: e.target.value }))} className={inputGlass} />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] uppercase font-bold text-gray-500 ml-1">Hasta</label>
                  <div className="flex gap-2">
                    <input type="date" value={fechasTemporales.fin} onChange={(e) => setFechasTemporales(f => ({ ...f, fin: e.target.value }))} className={inputGlass} />
                    <button onClick={aplicarFechas} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl transition-colors whitespace-nowrap">Aplicar</button>
                  </div>
                </div>
              </div>
            )}

            {lista.chips.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {lista.chips.map((chip) => (
                  <span key={chip.key} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/10 text-blue-800 text-[11px] font-bold border border-blue-500/20">
                    {chip.label}
                    <button onClick={chip.onRemove} className="hover:text-red-600 cursor-pointer">✕</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* BOTONES DE ACCIÓN MEJORADOS */}
          <div className="flex flex-wrap gap-3 mb-5 justify-end items-center">
            <button
              onClick={() => px.recalcular(filtrados)}
              disabled={px.calculando || loading}
              className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-extrabold tracking-wide transition-all shadow-md hover:shadow-lg cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-2"
            >
              {px.calculando ? "⏳ Calculando..." : "🧮 Recalcular precios"}
            </button>
            <button 
              onClick={exportarTodos} 
              className={`px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-extrabold tracking-wide shadow-md hover:shadow-lg transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${pendientesPrecio > 0 ? "ring-2 ring-amber-400 ring-offset-2 ring-offset-white" : ""}`}
            >
              📊 Exportar Excel
            </button>
          </div>

          {filtradosConRevision.length === 0 ? (
            <div className={`py-16 flex flex-col items-center gap-2 ${tema.textoSuave}`}>
              <div className="text-5xl">📂</div>
              <p className="text-sm font-bold">No hay ajustes para mostrar</p>
              <p className="text-xs font-medium">Cambia los filtros o el rango de fechas.</p>
            </div>
          ) : (
            <>
              <TablaAjustes
                tema={tema} lista={lista} visiblesRevision={visiblesRevision} detallePorAjuste={detallePorAjuste}
                precios={precios} px={px} loading={loading} estaVisto={estaVisto} esPermanente={esPermanente}
                abrirAjuste={abrirAjuste} exportarAjuste={exportarAjuste}
              />

              <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-4">
                <p className={`text-xs font-medium ${tema.textoSuave}`}>
                  Mostrando {fmt(desde + 1, 3)} - {fmt(Math.min(desde + POR_PAGINA, filtradosConRevision.length), 3)} de {fmt(filtradosConRevision.length, 3)} ajustes
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPagina(paginaActual - 1)} disabled={paginaActual <= 1} className={btnGlass}>Anterior</button>
                  <span className={`text-xs font-bold ${tema.textoNormal}`}>{paginaActual} / {totalPaginas}</span>
                  <button onClick={() => setPagina(paginaActual + 1)} disabled={paginaActual >= totalPaginas} className={btnGlass}>Siguiente</button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {seleccionado && (
        <ModalDetalle
          ajuste={seleccionado} lineas={lineasDe(detallePorAjuste, seleccionado)} precios={precios} tema={tema}
          vista={vistaModal} setVista={setVistaModal} onClose={cerrarModal} onExportar={exportarAjuste}
          visto={estaVisto(seleccionado.AJUS_ID)} esPermanente={esPermanente(seleccionado.AJUS_ID)}
          onTogglePermanente={() => togglePermanente(seleccionado.AJUS_ID)}
        />
      )}
    </div>
  );
}